import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { UserRole, AuthWrapperProps, canViewResource } from '@/types/auth';
import { isStaffRole } from '@/lib/roleRegistry';
import { useRoleRegistry } from '@/hooks/useRoleRegistry';
import { useAuthStore } from '@/stores/authStore';
import { TopNavbar } from '@/components/layouts/TopNavbar';
import { SidebarLayout } from '@/components/layouts/SidebarLayout';
import { DynamicSidebar } from '@/components/sidebar/DynamicSidebar';
import { platformHome, RESOURCE_BY_KEY, type Area } from '@/config/resources';
import { LoginPage } from './LoginPage';
import { currentPlatform, ePath, loginHref, tPath } from '@/utils/paths';
import { carrying, currentLocation, inactiveAccountRoute } from '@/lib/accountActivation';

// Where a denied user is sent back to: the home of the platform they are
// actually on, never terminus as the shell's implicit fallback. No platform in
// the URL means a cross-platform surface (/org, /superadmin), which
// `platformHome` lands on the org panel — `/` would bounce back here.
const currentPlatformHome = (): string => platformHome(currentPlatform());

// `area` renders the same sidebar the granted page would have had, so a user
// who lands here from a stale/shared link can still navigate elsewhere
// instead of hitting a dead end. Unknown only for AnyStaffRoute's "not staff
// at all" case, where no area's sidebar would make sense to show.
export const AccessDeniedPage: React.FC<{
  title: string;
  description: string;
  href: string;
  linkLabel: string;
  area?: Area;
}> = ({ title, description, href, linkLabel, area }) => {
  const body = (
    <main className="flex min-h-[calc(100vh-3.5rem)] items-center justify-center p-6">
      <div className="text-center">
        <h1 className="mb-4 text-4xl font-bold text-destructive">403</h1>
        <p className="mb-2 text-xl text-foreground">{title}</p>
        <p className="mb-4 text-muted-foreground">{description}</p>
        <a href={href} className="text-foreground hover:underline">
          {linkLabel}
        </a>
      </div>
    </main>
  );

  if (area) {
    return (
      <SidebarLayout sidebar={<DynamicSidebar area={area} />}>
        {body}
      </SidebarLayout>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <TopNavbar />
      {body}
    </div>
  );
};

export const AuthWrapper: React.FC<AuthWrapperProps> = ({
  children,
  role,
  resource,
  redirectOnUnauthorized = true,
  loginPath,
  requiredFeature,
}) => {
  const {
    user,
    permissions,
    isAuthenticated,
    isAuthVerified,
    org_features,
    error,
    refreshAuth,
    redirectToLogin,
    endInactiveSession,
    isRole
  } = useAuthStore();
  // Resource gating reads the role registry; wait for it rather than guessing.
  const { data: roleRegistry } = useRoleRegistry();
  const { pathname } = useLocation();
  const { t } = useTranslation();

  const redirecting = (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <p className="text-muted-foreground">{t('auth.wrapper.redirecting')}</p>
      </div>
    </div>
  );

  // Verification is global and monotonic: the first wrapper to mount does it,
  // and a nested wrapper (the /org and /superadmin panels gate twice — outer
  // panel key, inner section key) reuses the result instead of firing a second
  // /me. The render gate below deliberately ignores `isLoading`: that flag is
  // global and flips on EVERY refreshAuth, so gating on it made an outer
  // wrapper swap to the spinner and unmount its subtree, remounting the inner
  // wrapper, which refreshed again — the /org/overview infinite loop.
  useEffect(() => {
    if (!isAuthVerified) {
      refreshAuth();
    }
  }, [isAuthVerified, refreshAuth]);

  const inactiveRoute = isAuthVerified && isAuthenticated && user
    ? inactiveAccountRoute(user, pathname)
    : null;
  // `user` is a fresh object on every /me, so the effect keys on the two
  // fields the route reads instead of the reference — and passes exactly
  // those, so widening `inactiveAccountRoute`'s input stops compiling here
  // rather than silently reaching it as undefined.
  const userRole = user?.role;
  const userState = user?.state;
  useEffect(() => {
    const route = isAuthVerified && isAuthenticated && userRole
      ? inactiveAccountRoute({ role: userRole, state: userState }, pathname)
      : null;
    if (!route) return;
    if (route.kind === 'onboarding') {
      window.location.replace(carrying(route.path));
      return;
    }
    void endInactiveSession(route.error);
  }, [isAuthVerified, isAuthenticated, userRole, userState, pathname, endInactiveSession]);

  if (!isAuthVerified || (resource && !roleRegistry)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-foreground" />
          <p className="text-muted-foreground">{t('auth.wrapper.loading')}</p>
        </div>
      </div>
    );
  }

  // Resource gating (staff areas) resolves access from the backend registry;
  // role gating handles the entrepreneur portal. Staff resources log in via the
  // staff portal, so their primary role for the login redirect is SUPERADMIN.
  const primaryRole = resource
    ? UserRole.SUPERADMIN
    : Array.isArray(role)
      ? role[0]
      : (role ?? UserRole.SUPERADMIN);
  const hasRole = resource
    ? canViewResource(user?.role, resource, permissions)
    : Array.isArray(role)
      ? role.includes(user?.role as UserRole)
      : role
        ? isRole(role as UserRole)
        : isStaffRole(user?.role);

  const resolvedLoginPath = loginPath ?? (primaryRole === UserRole.ENTREPRENEUR ? ePath('/login') : loginHref());
  // AnyStaffRoute (no resource, no role) has no area to derive -- its
  // !hasRole means "not staff at all", so no area's sidebar applies.
  const deniedArea: Area | undefined = resource
    ? RESOURCE_BY_KEY[resource]?.area
    : role === UserRole.ENTREPRENEUR
      ? 'entrepreneur'
      : undefined;

  if (!isAuthenticated) {
    if (redirectOnUnauthorized) {
      redirectToLogin(primaryRole, currentLocation(), resolvedLoginPath);
      return redirecting;
    }
    return <LoginPage role={primaryRole} />;
  }

  if (inactiveRoute) {
    return redirecting;
  }

  if (!hasRole) {
    if (redirectOnUnauthorized) {
      return <AccessDeniedPage
        title={t('auth.wrapper.accessForbidden')}
        description={t('auth.wrapper.noPermission')}
        href={
          user?.role === UserRole.ENTREPRENEUR
            ? ePath('/dashboard')
            : user?.role === UserRole.EXPERT
              ? tPath('/committees')
              : currentPlatformHome()
        }
        linkLabel={t('auth.wrapper.returnToDashboard')}
        area={deniedArea}
      />;
    }
  }

  if (requiredFeature && !org_features.includes(requiredFeature)) {
    return <AccessDeniedPage
      title={t('auth.wrapper.featureNotAvailable')}
      description={t('auth.wrapper.noOrgAccess')}
      href={currentPlatformHome()}
      linkLabel={t('auth.wrapper.returnToDashboard')}
      area={deniedArea}
    />;
  }

  if (error && !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="bg-destructive/10 text-destructive p-4 rounded-lg">
            <p className="font-medium">{t('auth.wrapper.authError')}</p>
            <p className="text-sm mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};


// Staff areas gate on a per-resource basis, resolved from the backend role
// registry (single source of truth). Each route declares its resource; the
// committees route uses resource="committees" so experts (who have only that)
// pass while other staff resources stay hidden/forbidden for them. `resource`
// is required so a route that forgets to declare one fails to compile,
// instead of silently falling through to AnyStaffRoute's broader gate below.
interface StaffRouteProps {
  children: React.ReactNode;
  resource: string;
  requiredFeature?: string;
}

export const StaffRoute: React.FC<StaffRouteProps> = ({ resource, requiredFeature, children }) => (
  <AuthWrapper resource={resource} requiredFeature={requiredFeature} loginPath={loginHref()}>
    {children}
  </AuthWrapper>
);

// For the rare route that only needs "any authenticated staff role", not a
// specific resource grant -- e.g. /dashboard, whose only job is to redirect
// onward to whatever resource the role actually has. A distinct, explicitly
// named component rather than an omitted StaffRoute.resource, so this
// broader gate is visible at the call site, not inferred from an absence.
export const AnyStaffRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <AuthWrapper loginPath={loginHref()}>{children}</AuthWrapper>
);

export const EntrepreneurAuthWrapper: React.FC<Omit<AuthWrapperProps, 'role' | 'resource'>> = (props) => (
  <AuthWrapper {...props} role={UserRole.ENTREPRENEUR} loginPath={ePath('/login')} />
);
